import { useMemo, useState } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || '';

const initialForm = {
  name: '',
  email: '',
  relationship: 'player',
  evidenceType: 'x_account',
  evidenceValue: '',
  message: '',
  xUrl: '',
  profilePhotoUrl: '',
  primaryPosition: '',
  preferredFoot: '',
  nationality: '',
  yearOfBirth: '',
};

const labelClass = 'font-label text-[11px] font-semibold uppercase tracking-[0.12em] text-on-surface-variant';
const fieldClass = 'w-full rounded-lg bg-surface-container-highest border border-outline-variant/30 px-3 py-2.5 text-sm text-on-surface placeholder:text-on-surface-variant/40 transition-colors duration-150 focus:outline-none focus:border-primary/70 focus-visible:ring-2 focus-visible:ring-primary/40';

function TextInput({ label, value, onChange, type = 'text', required = false, placeholder = '' }) {
  return (
    <label className="block space-y-1.5">
      <span className={labelClass}>{label}</span>
      <input
        type={type}
        value={value}
        required={required}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
        className={fieldClass}
      />
    </label>
  );
}

function SelectInput({ label, value, onChange, children }) {
  return (
    <label className="block space-y-1.5">
      <span className={labelClass}>{label}</span>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className={fieldClass}
      >
        {children}
      </select>
    </label>
  );
}

function TextArea({ label, value, onChange, required = false, placeholder = '' }) {
  return (
    <label className="block space-y-1.5">
      <span className={labelClass}>{label}</span>
      <textarea
        value={value}
        required={required}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
        rows={4}
        className={`${fieldClass} resize-y`}
      />
    </label>
  );
}

function ActionButton({ active, icon, label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-2 rounded-lg border px-3 py-2.5 text-left text-xs font-bold transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/70 ${
        active
          ? 'border-primary/50 bg-primary/10 text-primary'
          : 'border-outline-variant/30 bg-surface-container-high text-on-surface-variant hover:text-on-surface hover:border-outline-variant/50'
      }`}
    >
      <span className="material-symbols-outlined text-[18px]">{icon}</span>
      <span>{label}</span>
    </button>
  );
}

export default function PlayerProfileActions({ player, profile }) {
  const [activeAction, setActiveAction] = useState('claim');
  const [form, setForm] = useState(initialForm);
  const [status, setStatus] = useState({ type: 'idle', message: '' });
  const [submitting, setSubmitting] = useState(false);

  const name = player?.player_name || player?.name || '';
  const club = player?.club_name || '';
  const isClaimed = profile?.profile_status === 'claimed';

  const actionCopy = useMemo(() => ({
    claim: {
      title: isClaimed ? 'Profile Claimed' : 'Claim Profile',
      submit: 'Submit Claim',
      success: 'Claim submitted for review.',
    },
    correction: {
      title: 'Suggest Correction',
      submit: 'Submit Correction',
      success: 'Correction submitted for review.',
    },
    privacy: {
      title: 'Privacy Request',
      submit: 'Submit Request',
      success: 'Privacy request submitted for review.',
    },
  }), [isClaimed]);

  function updateField(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  function playerPayload() {
    return {
      player_id: player?.player_id,
      player_name: name,
      club_name: club,
    };
  }

  async function postJson(path, payload) {
    const response = await fetch(`${API_BASE}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    const json = await response.json().catch(() => null);
    if (!response.ok || !json?.success) {
      throw new Error(json?.error || `HTTP ${response.status}`);
    }
    return json;
  }

  function buildPayload() {
    const common = {
      ...playerPayload(),
      name: form.name,
      email: form.email,
      relationship: form.relationship,
      message: form.message,
    };

    if (activeAction === 'claim') {
      return {
        path: '/api/player-profile/claims',
        payload: {
          ...common,
          claimant_name: form.name,
          claimant_email: form.email,
          evidence_type: form.evidenceType,
          evidence_value: form.evidenceValue,
        },
      };
    }

    const payload = {};
    if (activeAction === 'correction') {
      payload.stat_correction = form.message;
      if (form.xUrl) payload.x_url = form.xUrl;
      if (form.profilePhotoUrl) payload.profile_photo_url = form.profilePhotoUrl;
      if (form.primaryPosition) payload.primary_position = form.primaryPosition;
      if (form.preferredFoot) payload.preferred_foot = form.preferredFoot;
      if (form.nationality) payload.nationality = form.nationality;
      if (form.yearOfBirth) payload.year_of_birth = Number(form.yearOfBirth);
    }

    return {
      path: '/api/player-profile/change-requests',
      payload: {
        ...common,
        requester_name: form.name,
        requester_email: form.email,
        request_type: activeAction === 'privacy' ? 'privacy' : 'correction',
        payload,
      },
    };
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setSubmitting(true);
    setStatus({ type: 'idle', message: '' });

    try {
      const { path, payload } = buildPayload();
      await postJson(path, payload);
      setStatus({ type: 'success', message: actionCopy[activeAction].success });
      setForm(initialForm);
    } catch (error) {
      setStatus({ type: 'error', message: error.message });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="bg-surface-container p-5 sm:p-6 rounded-xl border border-outline-variant/30">
      <div className="flex items-center justify-between gap-4 mb-5">
        <div>
          <h4 className="font-headline text-lg font-bold text-on-surface">Profile Control</h4>
          <p className="text-xs text-on-surface-variant mt-1">
            {isClaimed ? 'Claimed profile' : 'Unclaimed profile'}
          </p>
        </div>
        <span className={`material-symbols-outlined ${isClaimed ? 'text-primary' : 'text-on-surface-variant/60'}`}>
          {isClaimed ? 'verified' : 'shield_person'}
        </span>
      </div>

      <div className="grid grid-cols-1 gap-2 mb-5">
        <ActionButton active={activeAction === 'claim'} icon="how_to_reg" label="Claim" onClick={() => setActiveAction('claim')} />
        <ActionButton active={activeAction === 'correction'} icon="edit_note" label="Correct" onClick={() => setActiveAction('correction')} />
        <ActionButton active={activeAction === 'privacy'} icon="privacy_tip" label="Privacy" onClick={() => setActiveAction('privacy')} />
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <h5 className="font-headline font-bold text-on-surface">{actionCopy[activeAction].title}</h5>
          <p className="text-xs leading-relaxed text-on-surface-variant mt-1">
            {activeAction === 'claim' && 'Approved claims unlock future player-owned profile updates.'}
            {activeAction === 'correction' && 'Stats corrections stay pending until source-checked.'}
            {activeAction === 'privacy' && 'Personal data removal and visibility requests are reviewed manually.'}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <TextInput label="Name" value={form.name} onChange={(value) => updateField('name', value)} required />
          <TextInput label="Email" type="email" value={form.email} onChange={(value) => updateField('email', value)} required />
        </div>

        <SelectInput label="Relationship" value={form.relationship} onChange={(value) => updateField('relationship', value)}>
          <option value="player">Player</option>
          <option value="club_official">Club official</option>
          <option value="representative">Representative</option>
          <option value="family">Family</option>
          <option value="supporter">Supporter</option>
        </SelectInput>

        {activeAction === 'claim' && (
          <>
            <SelectInput label="Evidence" value={form.evidenceType} onChange={(value) => updateField('evidenceType', value)}>
              <option value="x_account">X account</option>
              <option value="club_email">Club email</option>
              <option value="club_confirmation">Club confirmation</option>
              <option value="other">Other</option>
            </SelectInput>
            <TextInput label="Evidence Link or Note" value={form.evidenceValue} onChange={(value) => updateField('evidenceValue', value)} />
          </>
        )}

        {activeAction === 'correction' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <TextInput label="X URL" value={form.xUrl} onChange={(value) => updateField('xUrl', value)} placeholder="https://x.com/..." />
            <TextInput label="Photo URL" value={form.profilePhotoUrl} onChange={(value) => updateField('profilePhotoUrl', value)} />
            <TextInput label="Position" value={form.primaryPosition} onChange={(value) => updateField('primaryPosition', value)} />
            <SelectInput label="Foot" value={form.preferredFoot} onChange={(value) => updateField('preferredFoot', value)}>
              <option value="">Unknown</option>
              <option value="right">Right</option>
              <option value="left">Left</option>
              <option value="both">Both</option>
            </SelectInput>
            <TextInput label="Nationality" value={form.nationality} onChange={(value) => updateField('nationality', value)} />
            <TextInput label="Birth Year" type="number" value={form.yearOfBirth} onChange={(value) => updateField('yearOfBirth', value)} />
          </div>
        )}

        <TextArea
          label={activeAction === 'privacy' ? 'Request' : 'Message'}
          value={form.message}
          onChange={(value) => updateField('message', value)}
          required={activeAction !== 'correction'}
        />

        {status.message && (
          <div className={`rounded-lg border px-3 py-2 text-xs font-medium ${
            status.type === 'success'
              ? 'border-primary/30 bg-primary/10 text-primary'
              : 'border-error/40 bg-error/10 text-error'
          }`}>
            {status.message}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="w-full rounded-lg bg-primary px-4 py-3 text-sm font-black text-on-primary transition-all duration-150 hover:bg-primary-fixed disabled:opacity-60 disabled:hover:bg-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/70 focus-visible:ring-offset-2 focus-visible:ring-offset-surface-container"
        >
          {submitting ? 'Submitting...' : actionCopy[activeAction].submit}
        </button>
      </form>
    </div>
  );
}
